/** Date helpers used by Phase 5+ screens. Keep these stateless and tiny. */

/** Today as ISO date "YYYY-MM-DD" using the local timezone. */
export function todayISO(): string {
  return toISODate(new Date());
}

/** Convert a Date to ISO "YYYY-MM-DD" using the local timezone. */
export function toISODate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Add `days` to a Date and return ISO "YYYY-MM-DD". */
export function addDaysISO(base: Date, days: number): string {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

/** Format an ISO date as e.g. "Tue, 14 Apr". Returns empty string if invalid. */
export function fmtShortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const parts = iso.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return '';
  const [y, m, d] = parts as [number, number, number];
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
}

/**
 * An ISO INSTANT as a short local date + time, e.g. "07 Sep 2026, 14:54".
 *
 * <p>
 * Unlike the date-only helpers here, this one converts to the reader's
 * timezone on purpose. A planned date is a calendar day and must not shift; an
 * audit stamp is a real moment, and showing a UTC clock to somebody in Dublin
 * would be an hour wrong all summer.
 * </p>
 */
export function fmtInstant(iso: string | null | undefined): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Today as a long human label, e.g. "Tuesday, 14 April". */
export function fmtTodayLong(): string {
  return new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** Parse ISO "YYYY-MM-DD" as a LOCAL date. Invalid input yields an invalid Date. */
export function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return new Date(NaN);
  return new Date(y, m - 1, d);
}

/**
 * Whole days from `from` to `to`, or null when either is missing or unparseable.
 *
 * <p>
 * Used to move a set of dates together — shift one and the rest keep their
 * relative spacing. Compared as UTC midnights rather than by subtracting local
 * Dates, so a DST boundary between the two cannot make a clean day difference
 * divide to a fraction.
 * </p>
 */
export function shiftInDays(
  from: string | null | undefined,
  to: string | null | undefined,
): number | null {
  if (!from || !to) return null;
  const a = parseISODate(from);
  const b = parseISODate(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((utcB - utcA) / 86_400_000);
}
